import React from "react";
import { Box, Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import TuneOutlinedIcon from "@material-ui/icons/TuneOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import TodayOutlinedIcon from "@material-ui/icons/TodayOutlined";

const useStyles = makeStyles((theme) => ({
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: theme.spacing(1),
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
    },
  },
  metricCard: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    cursor: "pointer",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      borderColor: "#cbd5e1",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.25, 1.5),
    },
  },
  metricCardActive: {
    borderColor: "#ef7f1a !important",
    backgroundColor: "#fffaf5 !important",
    boxShadow: "0 0 0 1px #ef7f1a, 0 4px 12px rgba(239, 127, 26, 0.12) !important",
  },
  iconWrapper: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      width: 38,
      height: 38,
      borderRadius: 8,
    },
  },
  metricContent: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  metricValue: {
    fontWeight: 700,
    fontSize: "1.25rem",
    lineHeight: 1.2,
    color: "#0f172a",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.1rem",
    },
  },
  metricLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

export default function ProgramLogMetrics({
  logs = [],
  totalCount = 0,
  selectedModule = "",
  onSelectModule,
}) {
  const classes = useStyles();

  // Summary counts
  const invoiceCount = logs.filter((l) => l.module === "Invoice").length;
  const stockCount = logs.filter((l) => l.module === "StockAdjustment").length;
  const itemCount = logs.filter((l) => l.module === "Item").length;

  const todayIso = new Date().toISOString().slice(0, 10);
  const todayCount = logs.filter((l) => l.createdAt && l.createdAt.slice(0, 10) === todayIso).length;

  const items = [
    {
      id: "total",
      label: "Total Filtered",
      value: totalCount,
      icon: <ListAltOutlinedIcon style={{ color: "#ef7f1a", fontSize: 22 }} />,
      bg: "#fff7ed",
      module: "",
    },
    {
      id: "Invoice",
      label: "Invoices",
      value: selectedModule === "Invoice" ? totalCount : invoiceCount,
      icon: <ReceiptOutlinedIcon style={{ color: "#2563eb", fontSize: 22 }} />,
      bg: "#eff6ff",
      module: "Invoice",
    },
    {
      id: "StockAdjustment",
      label: "Stock Adj.",
      value: selectedModule === "StockAdjustment" ? totalCount : stockCount,
      icon: <TuneOutlinedIcon style={{ color: "#0284c7", fontSize: 22 }} />,
      bg: "#f0f9ff",
      module: "StockAdjustment",
    },
    {
      id: "Item",
      label: "Items Catalog",
      value: selectedModule === "Item" ? totalCount : itemCount,
      icon: <StorefrontOutlinedIcon style={{ color: "#0d9488", fontSize: 22 }} />,
      bg: "#f0fdfa",
      module: "Item",
    },
    {
      id: "today",
      label: "Today's Pulse",
      value: todayCount,
      icon: <TodayOutlinedIcon style={{ color: "#16a34a", fontSize: 22 }} />,
      bg: "#f0fdf4",
      module: null,
    },
  ];

  return (
    <Box className={classes.metricsGrid}>
      {items.map((item) => {
        const isActive = item.module !== null && selectedModule === item.module;
        return (
          <Paper
            key={item.id}
            elevation={0}
            className={`${classes.metricCard} ${isActive ? classes.metricCardActive : ""}`}
            onClick={() => {
              if (item.module !== null && onSelectModule) {
                onSelectModule(selectedModule === item.module ? "" : item.module);
              }
            }}
          >
            <div className={classes.iconWrapper} style={{ backgroundColor: item.bg }}>
              {item.icon}
            </div>
            <div className={classes.metricContent}>
              <Typography className={classes.metricValue}>
                {item.value.toLocaleString()}
              </Typography>
              <Typography className={classes.metricLabel}>
                {item.label}
              </Typography>
            </div>
          </Paper>
        );
      })}
    </Box>
  );
}

